import React, { useEffect, useState, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { QRCodeCanvas } from 'qrcode.react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { VisibilityBadge, PlanBadge, StatusBadge } from '../components/Badges';
import { getOpenCallsWithFilters, setFilters, resetFilters, updatePinnedStatus } from '../redux/openCallSlice';
import { fetchOpenCallsWithFilters, fetchOpenCalls, togglePinnedOpenCall } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import OpenCallDetailModal from '../components/OpenCallDetailModal';
import { EyeIcon } from '@heroicons/react/16/solid';
import RecordsLoader from '../components/RecordsLoader';
import ConfirmationDialog from '../components/ConfirmationDialog';
import ErrorModal from '../components/ErrorModal';
import parseImages from '../utils/parseImages';


const OpenCallList = () => {
    const dispatch = useDispatch();
    const { openCalls, status, pagination, error } = useSelector((state) => state.opencalls);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [showFilters, setShowFilters] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOpenCall, setSelectedOpenCall] = useState(null);
    const [loading, setLoading] = useState({});
    const [visibleOpenCall, setVisibleOpenCall] = useState(null);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(() => () => { });

    const [localFilters, setLocalFilters] = useState({
        open_call_title: '',
        open_call_type: '',
        industry_type: '',
        category: '',
        eligibility: '',
        gender: '',
        listing_type: '',
        fee_type: '',
        country: '',
        city: '',
        open_call_email: '',
        event_date_from: '',
        event_date_to: '',
        event_deadline_from: '',
        event_deadline_to: '',
        created_at_from: '',
        created_at_to: '',
        updated_at_from: '',
        updated_at_to: '',
        price_min: '',
        price_max: '',
        plan: '',
        visibility: '',
        pinned: '',
        organization_email: '',
        organization_title: '',
        first_name: '',
        last_name: '',
        organization_type: '',
        org_country: '',
        org_city: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'open_call_title',
            label: 'Open Call Title',
            type: 'text',
            placeholder: 'Enter open call title',
        },
        {
            key: 'type',
            label: 'Open Call Type',
            type: 'text',
            placeholder: 'Enter open call type',
        },
        {
            key: 'industry_type',
            label: 'Industry Type',
            type: 'text',
            placeholder: 'Enter industry type',
        },
        {
            key: 'category',
            label: 'Category',
            type: 'text',
            placeholder: 'Enter category',
        },
        {
            key: 'eligibility',
            label: 'Eligibility',
            type: 'select',
            options: [
                { value: 'Open', label: 'Open' },
                { value: 'Closed', label: 'Closed' },
            ],
        },
        {
            key: 'gender',
            label: 'Gender',
            type: 'select',
            options: [
                { value: 'All', label: 'All' },
                { value: 'Male', label: 'Male' },
                { value: 'Female', label: 'Female' },
                { value: 'Other', label: 'Other' },
            ],
        },
        {
            key: 'listing_type',
            label: 'Listing Type',
            type: 'select',
            options: [
                { value: 'Premium', label: 'Premium' },
                { value: 'Standard', label: 'Standard' },
            ],
        },
        {
            key: 'fee_type',
            label: 'Fee Type',
            type: 'select',
            options: [
                { value: 'paid', label: 'Paid' },
                { value: 'free', label: 'Free' },
            ],
        },
        {
            key: 'country',
            label: 'Country',
            type: 'text',
            placeholder: 'Enter country',
        },
        {
            key: 'city',
            label: 'City',
            type: 'text',
            placeholder: 'Enter city',
        },
        {
            key: 'open_call_email',
            label: 'Open Call Email',
            type: 'text',
            placeholder: 'Enter open call email',
        },
        {
            key: 'event_date_from',
            label: 'Event Date From',
            type: 'date',
        },
        {
            key: 'event_date_to',
            label: 'Event Date To',
            type: 'date',
        },
        {
            key: 'event_deadline_from',
            label: 'Event Deadline From',
            type: 'date',
        },
        {
            key: 'event_deadline_to',
            label: 'Event Deadline To',
            type: 'date',
        },
        {
            key: 'created_at_from',
            label: 'Created Date From',
            type: 'date',
        },
        {
            key: 'created_at_to',
            label: 'Created Date To',
            type: 'date',
        },
        {
            key: 'updated_at_from',
            label: 'Updated Date From',
            type: 'date',
        },
        {
            key: 'updated_at_to',
            label: 'Updated Date To',
            type: 'date',
        },
        {
            key: 'price_min',
            label: 'Min Price',
            type: 'number',
            placeholder: 'Enter minimum price',
            step: '0.01',
            min: 0,
        },
        {
            key: 'price_max',
            label: 'Max Price',
            type: 'number',
            placeholder: 'Enter maximum price',
            step: '0.01',
            min: 0,
        },
        {
            key: 'plan',
            label: 'Plan',
            type: 'select',
            options: [
                { value: '2', label: 'Pro' },
                { value: '1', label: 'Free (First Time)' },
                { value: '0', label: 'Free (After Paid)' },
            ],
        },
        {
            key: 'visibility',
            label: 'Visibility',
            type: 'select',
            options: [
                { value: '1', label: 'Visible' },
                { value: '0', label: 'Hidden' },
            ],
        },
        {
            key: 'pinned',
            label: 'Pinned',
            type: 'select',
            options: [
                { value: '1', label: 'Pinned' },
                { value: '0', label: 'Not Pinned' },
            ],
        },
        {
            key: 'organization_email',
            label: 'Organization Email',
            type: 'text',
            placeholder: 'Enter organization email',
        },
        {
            key: 'organization_title',
            label: 'Organization Title',
            type: 'text',
            placeholder: 'Enter organization title',
        },
        {
            key: 'first_name',
            label: 'First Name',
            type: 'text',
            placeholder: 'Enter first name',
        },
        {
            key: 'last_name',
            label: 'Last Name',
            type: 'text',
            placeholder: 'Enter last name',
        },
        {
            key: 'organization_type',
            label: 'Organization Type',
            type: 'text',
            placeholder: 'Enter organization type',
        },
        {
            key: 'org_country',
            label: 'Organization Country',
            type: 'text',
            placeholder: 'Enter organization country',
        },
        {
            key: 'org_city',
            label: 'Organization City',
            type: 'text',
            placeholder: 'Enter organization city',
        },
    ];

    // Fetch open calls with current filters and pagination
    const fetchOpenCallsList = useCallback(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };

        // Convert plan filter to plan_id for API
        if (params.plan !== '' && params.plan !== null && params.plan !== undefined) {
            params.plan_id = params.plan;
            delete params.plan;
        }

        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getOpenCallsWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchOpenCallsList();
    }, [fetchOpenCallsList]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleExport = async (format) => {
        try {
            // const exportParams = { ...localFilters, export: format };
            const exportParams = {
                ...localFilters,
                export: format
            };

            // Convert plan filter to plan_id for API
            if (exportParams.plan !== '' && exportParams.plan !== null && exportParams.plan !== undefined) {
                exportParams.plan_id = exportParams.plan;
                delete exportParams.plan;
            }

            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchOpenCallsWithFilters(exportParams);
            handleExportResponse(response, format, `open-calls.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export open calls. Please try again.');
        }
    };

    const handleRowClick = (openCall) => {
        setSelectedOpenCall(openCall);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedOpenCall(null);
    };

    const openConfirmationDialog = (message, action) => {
        setConfirmationMessage(message);
        setConfirmAction(() => action);
        setIsConfirmationOpen(true);
    };

    const closeConfirmationDialog = () => {
        setIsConfirmationOpen(false);
    };

    const handleToggleVisibility = (openCallId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus ? 'hide' : 'show'} this open call?`,
            async () => {
                setLoading((prev) => ({ ...prev, [openCallId]: true }));
                try {
                    await fetchOpenCalls(openCallId);
                    // Refresh open calls after toggling visibility
                    const params = {
                        page: currentPage,
                        page_size: pageSize,
                        ...localFilters,
                    };
                    Object.keys(params).forEach(key => {
                        if (params[key] === '' || params[key] === null || params[key] === undefined) {
                            delete params[key];
                        }
                    });
                    if (params.plan) {
                        params.plan_id = params.plan;
                        delete params.plan;
                    }
                    dispatch(getOpenCallsWithFilters(params));
                } catch (error) {
                    console.error('Error toggling visibility status:', error);
                } finally {
                    setLoading((prev) => ({ ...prev, [openCallId]: false }));
                    closeConfirmationDialog();
                }
            }
        );
    };


    const handleTogglePinned = (
        openCallId,
        currentPinnedStatus
    ) => {

        openConfirmationDialog(

            `Are you sure you want to ${currentPinnedStatus
                ? 'remove this open call from pinned section'
                : 'pin this open call on homepage'
            }?`,

            async () => {

                setLoading((prev) => ({
                    ...prev,
                    [`pin_${openCallId}`]: true
                }));

                try {

                    const response = await togglePinnedOpenCall(
                        openCallId
                    );

                    if (response?.data?.status === 1) {

                        // dispatch({
                        //     type: 'opencalls/updatePinnedStatus',
                        //     payload: {
                        //         id: openCallId,
                        //         pinned: response.data.data.pinned
                        //     }
                        // });

                        dispatch(
                            updatePinnedStatus({
                                id: openCallId,
                                pinned: response.data.data.pinned
                            })
                        );

                        fetchOpenCallsList();

                    }

                } catch (error) {

                    console.error(
                        'Error toggling pinned status:',
                        error
                    );

                } finally {

                    setLoading((prev) => ({
                        ...prev,
                        [`pin_${openCallId}`]: false
                    }));

                    closeConfirmationDialog();

                }

            }

        );

    };


    const generateImage = async (openCall) => {
        setVisibleOpenCall(openCall);
        setTimeout(async () => {
            const openCallId = openCall.open_call_id || openCall.id;
            const node = document.getElementById(`open-call-card-${openCallId}`);
            if (!node) {
                console.error('Card element not found');
                return;
            }
            try {
                const dataUrl = await toPng(node, { useCORS: true, cacheBust: true });
                saveAs(dataUrl, `open-call-${openCall.title}.png`);
            } catch (error) {
                console.error('Failed to generate image:', error);
                setErrorMessage('Failed to download the image. Please try again.');
                setErrorModalOpen(true);
            } finally {
                setVisibleOpenCall(null);
            }
        }, 300);
    };

    const closeErrorModal = () => {
        setErrorModalOpen(false);
        setErrorMessage('');
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading Open Calls, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Open Calls List</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Open Calls: {error || 'Unknown error'}
                </div>
            )}

            {/* Open Calls Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={openCalls || []}
                        getRowKey={(openCall) => openCall.open_call_id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'title',
                                label: 'Title',
                                flex: true,
                            },
                            {
                                key: 'organization_title',
                                label: 'Organization',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'plan_id',
                                label: 'Plan',
                                render: (value, item) => (
                                    <PlanBadge planId={value} planName={item.plan_name} />
                                ),
                            },
                            {
                                key: 'visibility',
                                label: 'Visibility',
                                render: (value) => <VisibilityBadge visibility={value} />,
                            },
                            {
                                key: 'viewed',
                                label: 'Views',
                                render: (value) => value || 0,
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'type',
                                label: 'Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'industry_type',
                                label: 'Industry Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'category',
                                label: 'Category',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'eligibility',
                                label: 'Eligibility',
                                render: (value) => <StatusBadge status={value} />,
                            },
                            {
                                key: 'gender',
                                label: 'Gender',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'listing_type',
                                label: 'Listing Type',
                                render: (value) => <StatusBadge status={value} />,
                            },
                            {
                                key: 'fee_type',
                                label: 'Fee Type',
                                render: (value) => <StatusBadge status={value} />,
                            },
                            {
                                key: 'price',
                                label: 'Price',
                                render: (value, item) => {
                                    if (item.fee_type === 'Paid' && value) {
                                        return `$${value}`;
                                    }
                                    return 'Free';
                                },
                            },
                            {
                                key: 'artistic_fields',
                                label: 'Artistic Fields',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'description',
                                label: 'Description',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'country',
                                label: 'Country',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'city',
                                label: 'City',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'location',
                                label: 'Location',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'email',
                                label: 'Email',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'phone',
                                label: 'Phone',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'web_link',
                                label: 'Web Link',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },
                            {
                                key: 'apply_now_link',
                                label: 'Apply Now Link',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },
                            {
                                key: 'instagram',
                                label: 'Instagram',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },

                            {
                                key: 'instagram_promotion_graphic',
                                label: 'Instagram Promotion Graphic',
                                render: (value, item) => {
                                    if (![2, 3].includes(item.plan_id) || !value) {
                                        return '—';
                                    }

                                    return (
                                        <div className="group relative w-fit">
                                            <a
                                                href={value}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    src={value}
                                                    alt="instagram-promotion"
                                                    className="h-20 w-20 rounded-xl border border-gray-200 object-cover transition hover:scale-105"
                                                />
                                            </a>

                                            <a
                                                href={value}
                                                download
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/80 text-white opacity-0 backdrop-blur-md transition group-hover:opacity-100"
                                                title="Download Image"
                                            >
                                                <svg
                                                    className="h-4 w-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 5v10m0 0l-4-4m4 4l4-4m-9 8h10"
                                                    />
                                                </svg>
                                            </a>
                                        </div>
                                    );
                                },
                            },
                            {
                                key: 'instagram_caption',
                                label: 'Instagram Caption',
                                render: (value, item) => {
                                    if (![2, 3].includes(item.plan_id)) {
                                        return '—';
                                    }

                                    return value || '—';
                                },
                            },
                            {
                                key: 'instagram_hashtags',
                                label: 'Instagram Hashtags',
                                render: (value, item) => {
                                    if (![2, 3].includes(item.plan_id)) {
                                        return '—';
                                    }

                                    return value || '—';
                                },
                            },
                            {
                                key: 'instagram_handle',
                                label: 'Instagram Handle',
                                render: (value, item) => {
                                    if (![2, 3].includes(item.plan_id)) {
                                        return '—';
                                    }

                                    return value || '—';
                                },
                            },


                            // {
                            //     key: 'images',
                            //     label: 'Image',
                            //     render: (value) => value ? (
                            //         <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            //             View Image
                            //         </a>
                            //     ) : '—',
                            // },

                            {
                                key: 'images',
                                label: 'Images',

                                render: (value) => {

                                    const imageArray = parseImages(value);

                                    if (!imageArray.length) return '—';

                                    return (

                                        <div className="flex flex-wrap gap-2">

                                            {imageArray.map((img, index) => (

                                                <div
                                                    key={index}
                                                    className="group relative"
                                                >

                                                    {/* IMAGE */}
                                                    <a
                                                        href={img}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={img}
                                                            alt={`open-call-${index}`}
                                                            className="h-16 w-16 rounded-xl border border-gray-200 object-cover transition hover:scale-105"
                                                        />
                                                    </a>

                                                    {/* DOWNLOAD BUTTON */}
                                                    <a
                                                        href={img}
                                                        download
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/80 text-white opacity-0 backdrop-blur-md transition group-hover:opacity-100"
                                                        title="Download Image"
                                                    >
                                                        <svg
                                                            className="h-4 w-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 5v10m0 0l-4-4m4 4l4-4m-9 8h10"
                                                            />
                                                        </svg>
                                                    </a>

                                                </div>

                                            ))}

                                        </div>
                                    );
                                },
                            },

                            {
                                key: 'not_on_profit',
                                label: 'Not for Profit',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'prize_summary',
                                label: 'Prize Summary',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'prize_details',
                                label: 'Prize Details',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'event_date',
                                label: 'Event Date',
                                render: (value) => formatDate(value),
                            },
                            {
                                key: 'event_deadline',
                                label: 'Event Deadline',
                                render: (value) => formatDate(value),
                            },
                            {
                                key: 'pinned',
                                label: 'Pinned',
                                render: (value) => value ? 'Yes' : 'No',
                            },
                            {
                                key: 'organization_type',
                                label: 'Organization Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'org_description',
                                label: 'Organization Description',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'org_phone',
                                label: 'Organization Phone',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'org_state',
                                label: 'Organization State',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'org_zip_code',
                                label: 'Organization Zip Code',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'plan_description',
                                label: 'Plan Description',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'plan_price',
                                label: 'Plan Price',
                                render: (value) => value ? `$${value}` : '—',
                            },
                            {
                                key: 'plan_mrp',
                                label: 'Plan MRP',
                                render: (value) => value ? `$${value}` : '—',
                            },
                            {
                                key: 'paid_status',
                                label: 'Paid Status',
                                render: (value) => value ? 'Paid' : 'Not Paid',
                            },
                            {
                                key: 'verified_organization',
                                label: 'Verified Organization',
                                render: (value) => value ? 'Yes' : 'No',
                            },
                            {
                                key: 'created_at',
                                label: 'Created At',
                                render: (value) => formatDate(value),
                            },
                            {
                                key: 'updated_at',
                                label: 'Updated At',
                                render: (value) => formatDate(value),
                            },
                        ]}
                        renderActions={(openCall, isExpanded) => (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRowClick(openCall);
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 hover:scale-110 border border-blue-200"
                                    title="View Details"
                                >
                                    <EyeIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const openCallId = openCall.open_call_id || openCall.id;
                                        handleToggleVisibility(openCallId, openCall.visibility);
                                    }}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 border ${openCall.visibility
                                        ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                        : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                        }`}
                                    title={openCall.visibility ? 'Deactivate' : 'Activate'}
                                    disabled={loading[openCall.id]}
                                >
                                    {loading[openCall.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {openCall.visibility ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            )}
                                        </svg>
                                    )}
                                </button>

                                {[2, 3].includes(openCall.plan_id) && (
                                    <div
                                        onClick={(e) => {

                                            e.stopPropagation();

                                            const openCallId =
                                                openCall.open_call_id || openCall.id;

                                            handleTogglePinned(
                                                openCallId,
                                                openCall.pinned
                                            );

                                        }}

                                        className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 cursor-pointer border
        ${openCall.pinned
                                                ? 'bg-green-500 border-green-500'
                                                : 'bg-gray-300 border-gray-300'
                                            }
    `}

                                        title={
                                            openCall.pinned
                                                ? 'Pinned on Homepage'
                                                : 'Not Pinned'
                                        }
                                    >

                                        {/* TOGGLE CIRCLE */}
                                        <span
                                            className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300
            ${openCall.pinned
                                                    ? 'translate-x-6'
                                                    : 'translate-x-1'
                                                }
        `}
                                        />

                                    </div>

                                )}

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        generateImage(openCall);
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-50 hover:bg-purple-100 text-purple-700 transition-all duration-200 hover:scale-110 border border-purple-200"
                                    title="Download Promo Image"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                </button>
                            </>
                        )}
                        emptyMessage="No open calls found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}

                    {/* Hidden Card Template for Image Generation */}
                    <div
                        id={`open-call-card-${visibleOpenCall?.open_call_id || visibleOpenCall?.id}`}
                        className="absolute top-0 left-0 shadow-lg overflow-hidden max-w-sm bg-black text-white"
                        style={{ zIndex: -1, opacity: visibleOpenCall ? 1 : 0, position: 'fixed' }}
                    >
                        {visibleOpenCall && (
                            <div className="relative p-6 space-y-4 flex flex-col justify-between">
                                <div>
                                    <h2 className="text-3xl font-bold mb-2">OPENCALL</h2>
                                    <h3 className="text-xl font-semibold mb-4">
                                        {visibleOpenCall.title}
                                    </h3>
                                </div>
                                <hr className="border-t-2 border-white my-1" />
                                <div className="space-y-2">
                                    <p>
                                        Type: <strong>{visibleOpenCall.type || "N/A"}</strong>
                                    </p>
                                    {visibleOpenCall.gender && (
                                        <p>
                                            Gender: <strong>{visibleOpenCall.gender || "N/A"}</strong>
                                        </p>
                                    )}
                                    {visibleOpenCall.eligibility && (
                                        <p>
                                            Eligibility: <strong>{visibleOpenCall.eligibility || "N/A"}</strong>
                                        </p>
                                    )}
                                    {visibleOpenCall.fee_type === "Paid" && (
                                        <p>
                                            Fees: <strong>${visibleOpenCall.price}</strong>
                                        </p>
                                    )}
                                </div>
                                {visibleOpenCall.event_deadline && (
                                    <div className="mt-4">
                                        <p>
                                            Deadline: <strong>{visibleOpenCall.event_deadline}</strong>
                                        </p>
                                    </div>
                                )}
                                <hr className="border-t-2 border-white my-1" />
                                <div className="mt-4 flex items-center gap-4">
                                    <QRCodeCanvas value={`https://opencallforartists.com/listing/${visibleOpenCall?.open_call_id || visibleOpenCall?.id}`} size={100} />
                                    <p>Scan this code to apply</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Open Call Detail Modal */}
                    <OpenCallDetailModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        openCalls={selectedOpenCall}
                    />

                    {/* Error Modal */}
                    <ErrorModal
                        isOpen={errorModalOpen}
                        onClose={closeErrorModal}
                        message={errorMessage}
                    />

                    {/* Confirmation Dialog */}
                    <ConfirmationDialog
                        isOpen={isConfirmationOpen}
                        message={confirmationMessage}
                        onConfirm={confirmAction}
                        onCancel={closeConfirmationDialog}
                    />
                </>
            )}
        </div>
    );
};

export default OpenCallList;
